// Guided onboarding checklist (OB-3). Renders the status contract OB-2 froze
// (PR #1231) and calls the existing check/confirm routes. It adds no route
// semantics of its own: the server owns the plan, the states and the evidence.
//
// Round 12, lane B3b: every reading comes from `checklistView` (state before
// role, one next line, one open count), every write goes through `runWrite`,
// every failure through `failureCopy`/`outcomeSentence`, and the Worker's
// slug-bearing receipt sentences are rebuilt by `face.receiptWords`.
//
// Receipts live inside the acting row's card, under the button, and take focus
// (receipt_card.ts). There is no top-of-page banner for this flow.

import { AdminApiError, type AdminClient } from "../api.js";
import { clear, h } from "../dom.js";
import type {
  Company,
  CompanySlug,
  CompanyType,
  OnboardingChecklistStep,
  OnboardingReceipt,
  OnboardingRunResponse,
  OnboardingStatus,
  OnboardingStepKey,
  StaffRole,
  TokenIssueResponse,
} from "../types.js";
import {
  appendReceipt,
  renderReceiptCard,
  renderReceiptFailure,
} from "../ui/receipt_card.js";
import { dateText, timeAgoText } from "../ui/display-words.js";
import * as face from "../ui/face.js";
import {
  checklistView,
  OUTSIDE_CONSOLE_STEP_KEYS,
  type ChecklistStepView,
} from "../ui/facts.js";
import {
  AuthoredRefusal,
  failureCopy,
  outcomeSentence,
} from "../ui/failure.js";
import { sentence, navLabel } from "../ui/sentences.js";
import {
  forgetPossiblySent,
  rememberPossiblySent,
  runWrite,
  type WriteOutcome,
} from "../ui/write.js";
import { INVITATION_REVOKED_COPY } from "./invitations.js";
import { entityScopeChoices } from "./tokens.js";

function showDetails(detail: string): HTMLElement {
  return h("details", { class: "disclosure", "data-role": "show-details" }, [
    h("summary", {}, ["Show details"]),
    h("p", {}, [detail]),
  ]);
}

/** The only two rows a person may tick (brief §1; status contract). */
const HAND_STEP_KEYS: ReadonlySet<OnboardingStepKey> = new Set([
  "hub_side_mapping",
  "tenant_portal",
]);

const REASON_COPY: Record<string, string> = {
  no_longer_true: "This was done before, but it is not true now.",
  not_found: "We could not find what this step needs.",
  not_allowed: "The system is not allowed to do this.",
  check_error: "The check could not finish.",
};

const STATE_COPY: Record<string, { word: string; icon: string }> = {
  done: { word: "Done", icon: "✔" },
  waiting: { word: "Waiting", icon: "○" },
  blocked: { word: "Blocked", icon: "⊘" },
  failed: { word: "Failed", icon: "✖" },
};

const API_KEY_LIFETIME_MS = 90 * 24 * 60 * 60 * 1000;
/**
 * The Worker accepts a Hosted Link session within `EXCHANGE_WINDOW_MS` (30
 * minutes), so an invitation minted for a day claimed a lifetime it did not
 * have (rd R11 R6-17). The checklist and the Invitations page share the words,
 * and the decided sentence lives in `ui/sentences.ts` (round 11 §4).
 */
const INVITE_LIFETIME_MS = 30 * 60 * 1000;
const ISSUED_FROM_CHECKLIST_LABEL = "Issued from the onboarding checklist";
const INVITE_LABEL = "Issued from the onboarding checklist";

/**
 * The receipt for an invitation the checklist just issued. Its `next` is the
 * person connecting the bank, not the owner: the next step belongs to whoever
 * opens the link (rd r13 L3.5). Exported so the words are pinned by a test
 * without mounting the whole page.
 */
export function invitationIssuedReceipt(company: CompanySlug): OnboardingReceipt {
  return {
    happened: `Issued a link invitation for ${plainSlug(company)}.`,
    unchanged: "No other settings changed.",
    next: {
      // The next step is the person connecting the bank, not the owner: this
      // receipt used to read "Next: you approve or complete the next step."
      // over an invitation whose next move is the bank linker's (rd r13
      // L3.5). "bank_linker" is the contract's own `who` word.
      who: "bank_linker",
      text: "Send this link to the person who will connect the bank. It works for 30 minutes. It is shown only once.",
    },
  };
}

/**
 * The portal's title for one step. `face.checklistStep` capitalises the key's
 * words, which turns the acronym into "Api key"; the console writes "API key"
 * (round-2 review M6). Every step title on this page goes through here.
 */
export function checklistStepTitle(key: OnboardingStepKey): string {
  return face.checklistStep(key).replace(/\bApi\b/g, "API");
}

/**
 * The Worker's catalogue says `who: "am"` for bank-account mapping, but either
 * role may do it (owner decision, rd R11 R3-13p). `who` is a closed contract
 * enum, so the console says so here and the Worker's value is untouched.
 */
const ACCOUNTS_MAPPED_WHO = "the owner or account manager";

export interface CompanyChecklistPageOptions {
  client: AdminClient;
  root: HTMLElement;
  company: CompanySlug;
  role: StaffRole;
  companyType?: CompanyType;
  companyLabel?: string;
  isCurrent?: () => boolean;
}

interface PinnedReceipt {
  key: OnboardingStepKey | "header";
  node: HTMLElement;
}

/** Render one company's checklist, running a check first (brief §1). */
export async function renderCompanyChecklistPage(
  opts: CompanyChecklistPageOptions,
): Promise<void> {
  const { client, root, company, role } = opts;
  const stillCurrent = (): boolean => opts.isCurrent?.() ?? true;

  let status: OnboardingStatus | null = null;
  let lastCheckedAt: string | null = null;
  let pinned: PinnedReceipt | null = null;
  let companyType = opts.companyType;
  /** The display names the page already reads, in the one shell shape. */
  let directory = face.createCompanyDirectory([], { complete: false });
  /**
   * The access keys that existed when this page was rendered (round 3: captured
   * at render, not at press). A key that appears after the person arrived is
   * therefore "not one this page held" — which is what lets the check below find
   * a key that landed after the render, instead of treating it as pre-existing
   * and telling the person to press Issue again. `ok: false` means the read
   * failed, so a read-back cannot tell a new key from an old one and says
   * unknown.
   */
  let keyBaseline: { ok: boolean; ids: ReadonlySet<string> } | null = null;
  let keyBaselineRead: Promise<{ ok: boolean; ids: ReadonlySet<string> }> | null = null;
  function captureKeyBaseline(): Promise<{ ok: boolean; ids: ReadonlySet<string> }> {
    if (keyBaselineRead === null) {
      keyBaselineRead = (async () => {
        try {
          const { tokens } = await client.listTokens();
          const held = { ok: true, ids: new Set(tokens.map((token) => token.id)) };
          keyBaseline = held;
          return held;
        } catch {
          const held = { ok: false, ids: new Set() as ReadonlySet<string> };
          keyBaseline = held;
          return held;
        }
      })();
    }
    return keyBaselineRead;
  }
  async function readKeyBaseline(): Promise<{ ok: boolean; ids: ReadonlySet<string> }> {
    return await captureKeyBaseline();
  }

  clear(root);
  root.append(h("p", { class: "muted" }, ["Checking this company…"]));
  // At render, before any press of this visit.
  void captureKeyBaseline();

  const first = await Promise.all([checkOnOpen(), resolveCompany()]).then(
    ([checked]) => checked,
  );
  if (!stillCurrent()) return;
  if (first === "no-run") {
    paintNoRun();
    return;
  }
  status = first.status;
  lastCheckedAt = first.checkedAt;
  paint();

  async function checkOnOpen(): Promise<
    | { status: OnboardingStatus; checkedAt: string }
    | "no-run"
  > {
    const held: { value: OnboardingRunResponse | undefined } = { value: undefined };
    const outcome = await runWrite({
      key: `checklist-open:${plainSlug(company)}`,
      scope: root,
      gate: () => null,
      send: () => client.checkOnboardingRun(company),
      afterLanded: (response) => {
        held.value = response;
      },
      stillCurrent,
    });
    if (outcome.kind === "landed") {
      if (held.value !== undefined) {
        return {
          status: held.value.status,
          checkedAt: new Date().toISOString(),
        };
      }
    }
    const error = outcome.kind === "landed" ? undefined : outcome.error;
    if (error instanceof AdminApiError && error.status === 403) throw error;
    if (error instanceof AdminApiError && error.status === 404) return "no-run";
    // A failed check must not hide the recorded truth we already have.
    try {
      const recorded = await client.getOnboardingStatus(company);
      return { status: recorded, checkedAt: "" };
    } catch (secondError) {
      if (secondError instanceof AdminApiError && secondError.status === 403) {
        throw secondError;
      }
      if (secondError instanceof AdminApiError && secondError.status === 404) {
        return "no-run";
      }
      throw secondError;
    }
  }

  function paintNoRun(): void {
    clear(root);
    root.append(
      h("header", { class: "page-header" }, [
        h("h1", { class: "page-title", "data-role": "checklist-title" }, [
          headingLabel(),
        ]),
        h("p", { class: "lede", "data-role": "checklist-company" }, [
          companyFace(),
        ]),
      ]),
      h("div", { class: "empty empty-state", "data-role": "checklist-no-run" }, [
        "There is no open onboarding run for this company. Add the company first, or open one that already has a checklist.",
      ]),
    );
  }

  /** The page title is the nav label (the registry names the page once). */
  function headingLabel(): string {
    return navLabel("company-checklist");
  }

  /** The company reads as its name; the slug is never on the face. */
  function companyFace(): string {
    return face.company(company, directory, "sentence");
  }

  /**
   * The display name, once. The shell hands this page the short label (the
   * slug uppercased), so the readable name comes from the company list.
   */
  async function resolveCompany(): Promise<void> {
    try {
      const { companies } = await client.listCompanies();
      directory = face.createCompanyDirectory(
        companies.map((row: Company) => ({ slug: row.slug, displayName: row.displayName })),
        { complete: true },
      );
      const match = companies.find((row: Company) => row.slug === company);
      companyType = match?.companyType ?? companyType;
    } catch {
      // A missing name is not a failed checklist: "this company" is honest.
    }
  }

  function paint(): void {
    const current = status;
    if (current === null) return;
    const view = checklistView(current, role);
    clear(root);

    root.append(
      h("header", { class: "page-header" }, [
        h("h1", { class: "page-title", "data-role": "checklist-title" }, [
          headingLabel(),
        ]),
        h("p", { class: "lede", "data-role": "checklist-company" }, [
          `${companyFace()} · ${current.mode === "existing" ? "Existing books" : "New company"}`,
        ]),
      ]),
    );

    root.append(renderHeader(current, view));

    const groups = groupSteps(current.steps);
    for (const group of groups) {
      const section = h("section", {
        class: "checklist-group card",
        "data-role": "checklist-group",
        "data-group": group.name,
      });
      section.append(
        h("h2", { class: "section-title" }, [
          group.name === "core" ? "Setup" : group.name,
        ]),
      );
      for (const step of group.steps) {
        const stepView = view.steps.find((row) => row.key === step.key);
        if (stepView === undefined) continue;
        section.append(renderStep(step, stepView, current));
      }
      root.append(section);
    }

    // Consume the pinned receipt after it has been attached to its own card.
    pinned = null;
  }

  function renderHeader(current: OnboardingStatus, view: ReturnType<typeof checklistView>): HTMLElement {
    const header = h("section", {
      class: "checklist-header card",
      "data-role": "checklist-header",
    });
    header.append(
      h("p", { class: "checklist-progress", "data-role": "checklist-progress" }, [
        `${String(current.progress.done)} of ${String(current.progress.total)} done`,
      ]),
    );
    // ONE open count, from `checklistView`: it excludes `go_live` itself and
    // counts only required rows, so the header and the next-step block cannot
    // disagree (rc-4 A2, HIGH).
    if (view.openRequired > 0) {
      header.append(
        h("p", { class: "muted", "data-role": "checklist-required-open" }, [
          `${String(view.openRequired)} required ${view.openRequired === 1 ? "step is" : "steps are"} still open before go-live.`,
        ]),
      );
    }

    header.append(renderNext(current, view));

    const checkNow = h(
      "button",
      {
        type: "button",
        class: "btn btn-secondary",
        "data-action": "check-now",
        "aria-label": "Check the checklist again",
      },
      ["Check now"],
    );
    checkNow.addEventListener("click", () => {
      void runCheck("header", header);
    });
    header.append(
      h("div", { class: "checklist-check-row" }, [
        checkNow,
        h("span", {
          class: "muted",
          "data-role": "last-checked",
          "data-checked-at": lastCheckedAt ?? "",
        }, [
          lastCheckedAt === null || lastCheckedAt === ""
            ? "Showing the last recorded result; no check ran."
            : // The same time words as the step evidence below (rd r13 review 1,
              // item 4): a check that just ran is "just now", not a bare day.
              `Last checked ${evidenceWhen(lastCheckedAt)}.`,
        ]),
      ]),
    );

    if (pinned !== null && pinned.key === "header") {
      appendReceipt(header, pinned.node);
    }
    return header;
  }

  function renderNext(
    current: OnboardingStatus,
    view: ReturnType<typeof checklistView>,
  ): HTMLElement {
    return nextBlock(current.next, view, role);
  }

  function renderStep(
    step: OnboardingChecklistStep,
    view: ChecklistStepView,
    current: OnboardingStatus,
  ): HTMLElement {
    const card = h("article", {
      class: `checklist-step checklist-step-${view.state} card`,
      "data-role": "checklist-step",
      "data-step-key": step.key,
      "data-step-state": view.state,
    });

    const state = STATE_COPY[view.state] ?? { word: view.state, icon: "•" };
    card.append(
      h("div", { class: "checklist-step-head" }, [
        h("span", { class: "checklist-step-icon", "aria-hidden": "true" }, [state.icon]),
        h("h3", { class: "checklist-step-title" }, [checklistStepTitle(step.key)]),
        h("span", { class: "checklist-step-state", "data-role": "step-state" }, [
          state.word,
        ]),
        h("span", { class: "muted checklist-step-required" }, [
          step.required ? "Required" : "Optional",
        ]),
      ]),
    );

    card.append(
      h("p", { class: "checklist-step-who", "data-role": "step-who" }, [
        waitingCopy(step, view, role),
      ]),
    );

    if (step.key === "accounts_mapped" && view.state !== "done") {
      card.append(
        h("p", {
          class: "muted",
          "data-role": "accounts-mapped-instruction",
        }, [sentence("first_bank_mapping", role)]),
      );
    }

    if (view.state === "done") {
      card.append(
        h("p", { class: "checklist-step-evidence", "data-role": "step-evidence" }, [
          evidenceCopy(step),
        ]),
      );
    }

    if (view.state === "failed") {
      const reason = step.reason ?? "check_error";
      card.append(
        h("p", { class: "checklist-step-failure", "data-role": "step-failure" }, [
          REASON_COPY[reason] ?? "The check failed.",
        ]),
      );
      if (step.retrySafe === true) {
        const retry = h(
          "button",
          {
            type: "button",
            class: "btn btn-secondary",
            "data-action": "retry-step",
            "aria-label": face.ariaFor(`Try again: ${checklistStepTitle(step.key)}`),
          },
          ["Try again"],
        );
        retry.addEventListener("click", () => {
          void runCheck(step.key, card);
        });
        card.append(h("div", { class: "card-actions" }, [retry]));
      }
    }

    if (step.key === "go_live") {
      card.append(renderGoLiveLock(current));
    } else if (HAND_STEP_KEYS.has(step.key)) {
      card.append(renderHandStep(step.key, view, card));
    }

    if (step.key === "api_key" && current.capabilities.apiKey) {
      card.append(renderApiKeyAction(view, card));
    }
    if (step.key === "link_invitation") {
      card.append(renderInvitationAction(view, card));
    }

    if (pinned !== null && pinned.key === step.key) {
      appendReceipt(card, pinned.node);
    }
    return card;
  }

  function renderGoLiveLock(current: OnboardingStatus): HTMLElement {
    // The locked list is the same required-open set the header counts.
    const open = current.steps.filter(
      (step) => step.key !== "go_live" && step.required && step.state !== "done",
    );
    const host = h("div", {
      class: "checklist-go-live",
      "data-role": "go-live",
      "data-locked": open.length > 0 ? "true" : "false",
    });
    if (open.length === 0) {
      host.append(
        h("p", {}, [
          "All required steps are done. The go-live action itself is not built yet.",
        ]),
      );
      return host;
    }
    host.append(
      h("p", {}, ["Locked until these required steps are done:"]),
      h(
        "ul",
        { "data-role": "go-live-open" },
        open.map((step) => h("li", { "data-step-key": step.key }, [checklistStepTitle(step.key)])),
      ),
    );
    return host;
  }

  function renderHandStep(
    key: OnboardingStepKey,
    view: ChecklistStepView,
    scope: HTMLElement,
  ): HTMLElement {
    const host = h("div", { class: "card-actions", "data-role": "hand-step" });
    // The STATE is checked before the role: a Done step never says "Waiting
    // for the owner." (rc-4 A1, HIGH).
    if (view.state === "done") return host;
    // The console's tick is the owner's, whatever the contract's `who` says
    // (the real catalogue says hub_team / portal_team for these two rows, and
    // `ui/facts.ts` only offers an action when `who` is the reader). A reader
    // who is not the owner reads the decided waiting sentence on the one
    // waiter line above; there is no second "Waiting" line here (round 2 M5).
    if (role !== "owner") return host;
    const action = view.action ?? "Mark it done";
    const button = h(
      "button",
      {
        type: "button",
        class: "btn btn-primary",
        "data-action": "confirm-step",
        "data-step-key": key,
        "aria-label": face.ariaFor(action),
      },
      [action],
    );
    button.addEventListener("click", () => {
      void confirmStep(key, scope);
    });
    host.append(button);
    return host;
  }

  function renderApiKeyAction(
    view: ChecklistStepView,
    scope: HTMLElement,
  ): HTMLElement {
    const host = h("div", { class: "card-actions", "data-role": "api-key-action" });
    if (view.state === "done") return host;
    if (!status?.capabilities.apiKey) return host;
    if (view.action === null) {
      // The one waiter line above already says whose step this is (round 2 M5).
      return host;
    }
    const button = h(
      "button",
      {
        type: "button",
        class: "btn btn-primary",
        "data-action": "issue-api-key",
        "aria-label": face.ariaFor(view.action),
      },
      [view.action],
    );
    button.addEventListener("click", () => {
      void issueApiKey(scope);
    });
    host.append(button);
    return host;
  }

  function renderInvitationAction(
    view: ChecklistStepView,
    scope: HTMLElement,
  ): HTMLElement {
    const host = h("div", {
      class: "card-actions",
      "data-role": "invitation-action",
    });
    if (view.state === "done") return host;
    if (view.action === null) {
      // The one waiter line above already says whose step this is (round 2 M5).
      return host;
    }
    const button = h(
      "button",
      {
        type: "button",
        class: "btn btn-secondary",
        "data-action": "issue-link-invitation",
        "aria-label": face.ariaFor(view.action),
      },
      [view.action],
    );
    button.addEventListener("click", () => {
      void issueInvitation(scope);
    });
    host.append(button, h("p", {
      class: "muted",
      "data-role": "invitation-lifetime",
    }, [`The link ${sentence("bank_link_works_for", role)}.`]));
    return host;
  }

  /** A Worker receipt, worded by the portal and never by the Worker's slug. */
  function receiptCard(receipt: OnboardingReceipt): HTMLElement {
    return renderReceiptCard(receipt, {
      words: face.receiptWords(receipt, directory, role),
    });
  }

  /** The honest "what else changed" line for a failed write. */
  function unchangedFor(outcome: WriteOutcome): string {
    if (outcome.kind === "refused") {
      // Only a refusal that sent nothing may say nothing changed.
      return outcomeSentence({ kind: "refused", status: 0 }, role);
    }
    return outcomeSentence(outcome, role);
  }

  function receiptFailureFromOutcome(outcome: WriteOutcome): HTMLElement {
    const error = outcome.kind === "landed" ? undefined : outcome.error;
    const copy =
      error === undefined
        ? { sentence: outcomeSentence(outcome, role), detail: null }
        : failureCopy(error, { role, context: "write" });
    const node = renderReceiptFailure(copy.sentence, unchangedFor(outcome));
    if (copy.detail !== null) node.append(showDetails(copy.detail));
    return node;
  }

  /**
   * A credential issue's failure receipt. An issue can mint a one-time secret,
   * so an unknown outcome reads the possibly-sent sentence, never the shared
   * "Refresh to see the latest, then try again.": a reload forgets the
   * possibly-sent memory and is the one path that can mint a second credential
   * (round 2 M1). The person's way out is the page's own read, added by the
   * caller.
   */
  function issueFailureFromOutcome(outcome: WriteOutcome): HTMLElement {
    if (outcome.kind === "landed") {
      const node = renderReceiptFailure(
        outcomeSentence({ kind: "unknown", blocked: true }, role),
        outcomeSentence({ kind: "unknown", blocked: true }, role),
      );
      return node;
    }
    if (outcome.kind === "unknown") {
      const blockedSentence = outcomeSentence({ kind: "unknown", blocked: true }, role);
      const node = renderReceiptFailure(blockedSentence, blockedSentence);
      if (outcome.error !== undefined) {
        const detail = failureCopy(outcome.error, { role, context: "write" }).detail;
        if (detail !== null) node.append(showDetails(detail));
      }
      return node;
    }
    const copy =
      outcome.error === undefined
        ? { sentence: outcomeSentence(outcome, role), detail: null }
        : failureCopy(outcome.error, { role, context: "write" });
    const node = renderReceiptFailure(copy.sentence, unchangedFor(outcome));
    if (copy.detail !== null) node.append(showDetails(copy.detail));
    return node;
  }

  async function runCheck(
    pinKey: OnboardingStepKey | "header",
    scope: HTMLElement,
  ): Promise<void> {
    const held: { value: OnboardingRunResponse | undefined } = { value: undefined };
    const outcome = await runWrite({
      key: `checklist-check:${plainSlug(company)}:${pinKey}`,
      scope,
      gate: () => null,
      send: () => client.checkOnboardingRun(company),
      afterLanded: (response) => {
        held.value = response;
      },
      stillCurrent,
    });
    if (!stillCurrent()) return;
    if (outcome.kind === "landed") {
      if (held.value !== undefined) {
        status = held.value.status;
        lastCheckedAt = new Date().toISOString();
        pinned = { key: pinKey, node: receiptCard(held.value.receipt) };
        paint();
      }
      return;
    }
    pinned = { key: pinKey, node: receiptFailureFromOutcome(outcome) };
    paint();
  }

  async function confirmStep(
    key: OnboardingStepKey,
    scope: HTMLElement,
  ): Promise<void> {
    const held: { value: OnboardingRunResponse | undefined } = { value: undefined };
    const outcome = await runWrite({
      key: `checklist-confirm:${plainSlug(company)}:${key}`,
      scope,
      gate: () => null,
      send: () => client.confirmOnboardingStep(company, key),
      afterLanded: (response) => {
        held.value = response;
      },
      stillCurrent,
    });
    if (!stillCurrent()) return;
    if (outcome.kind === "landed") {
      if (held.value !== undefined) {
        status = held.value.status;
        pinned = { key, node: receiptCard(held.value.receipt) };
        paint();
      }
      return;
    }
    pinned = { key, node: receiptFailureFromOutcome(outcome) };
    paint();
  }

  async function issueApiKey(scope: HTMLElement): Promise<void> {
    const companyRow = await resolveCompanyType();
    if (!stillCurrent()) return;
    const scopes = entityScopeChoices(companyRow ?? "")
      .filter((choice) => choice.checked)
      .map((choice) => choice.scope);
    if (scopes.length === 0) {
      pinned = {
        key: "api_key",
        node: renderReceiptFailure(
          failureCopy(new AuthoredRefusal("No API key scopes apply to this company."), {
            role,
            context: "write",
          }).sentence,
          unchangedFor({ kind: "refused", status: 0 }),
        ),
      };
      paint();
      return;
    }
    const key = `checklist-api-key:${plainSlug(company)}`;
    // What this page already knew BEFORE the press (round 2 M4).
    const baseline = await readKeyBaseline();
    if (!stillCurrent()) return;
    const held: { issued: TokenIssueResponse | undefined } = { issued: undefined };
    const outcome = await runWrite({
      key,
      scope,
      gate: () => null,
      send: () =>
        client.issueToken({
          company,
          scopes,
          expiresAt: new Date(Date.now() + API_KEY_LIFETIME_MS).toISOString(),
          label: ISSUED_FROM_CHECKLIST_LABEL,
        }),
      // A lost answer may mean the key exists: look for a checklist key this
      // page did not hold before claiming it landed.
      readBack: async () => {
        if (!baseline.ok) return false;
        const { tokens } = await client.listTokens();
        return tokens.some(
          (token) =>
            token.company === company &&
            token.label === ISSUED_FROM_CHECKLIST_LABEL &&
            !baseline.ids.has(token.id),
        );
      },
      afterLanded: (value) => {
        held.issued = value;
      },
      stillCurrent,
      blockWhenPossiblySent: true,
    });
    if (outcome.kind === "landed" && !stillCurrent()) {
      // The issue landed after the page moved on, so the secret is gone. The
      // next visit must take the check path, never offer a fresh issue
      // (round 2 M2).
      rememberPossiblySent(key);
      return;
    }
    if (!stillCurrent()) return;
    if (outcome.kind === "landed" && held.issued !== undefined) {
      const created: TokenIssueResponse = held.issued;
      const receipt: OnboardingReceipt = {
        happened: `Issued an API key for ${plainSlug(company)}.`,
        unchanged: "No other settings changed.",
        next: {
          // The contract's `who` vocabulary (`receiptNextFromStatus` sends
          // "owner"); "Owner" matched nothing and read "someone" (rd r12 F1).
          who: "owner",
          text: "Give the key to the system that reads the books. It is shown only once.",
        },
      };
      const node = renderReceiptCard(receipt, {
        words: face.receiptWords(receipt, directory, role),
        oneTime: { label: "API key (copy it now)", value: created.token },
        oneTimeRole: "receipt-one-time-api-key",
      });
      await refreshAfterAction("api_key", node);
      return;
    }
    if (outcome.kind === "landed") {
      // The read-back found a key this page did not hold, but its secret never
      // reached the page.
      pinned = {
        key: "api_key",
        node: renderReceiptFailure(
          "The key was created, but its secret cannot be shown again.",
          `Revoke it from ${navLabel("tokens")} and create a new one if you did not copy it.`,
        ),
      };
      paint();
      return;
    }
    pinned = { key: "api_key", node: issueFailureFromOutcome(outcome) };
    if (outcome.kind === "unknown") {
      pinned.node.append(adoptKeyButton(key));
    }
    paint();
  }

  /**
   * After a lost answer, read the page's own list: a key this page did not hold
   * before the press means it landed, and the retry stays blocked until this
   * settles it.
   */
  function adoptKeyButton(key: string): HTMLElement {
    const button = h(
      "button",
      {
        type: "button",
        class: "btn btn-secondary",
        "data-action": "check-issued-key",
      },
      ["Check access keys"],
    );
    button.addEventListener("click", () => {
      void (async () => {
        button.disabled = true;
        let tokensFound: { found: boolean };
        try {
          const { tokens } = await client.listTokens();
          tokensFound = {
            found: tokens.some(
              (token) =>
                token.company === company &&
                token.label === ISSUED_FROM_CHECKLIST_LABEL &&
                token.revokedAt === null &&
                !(keyBaseline?.ids.has(token.id) ?? false),
            ),
          };
        } catch (error) {
          pinned = {
            key: "api_key",
            node: renderReceiptFailure(
              failureCopy(error, { role, context: "read" }).sentence,
              unchangedFor({ kind: "refused", status: 0 }),
            ),
          };
          paint();
          return;
        }
        forgetPossiblySent(key);
        const found = tokensFound.found;
        const node = renderReceiptFailure(
          found
            ? "The key was created, but its secret cannot be shown again."
            : unchangedFor({ kind: "refused", status: 0 }),
          found
            ? `Revoke it from ${navLabel("tokens")} and create a new one if you did not copy it.`
            : "Press Issue again when you are ready.",
        );
        if (found) {
          // The key exists, so the step is no longer open: re-read the page's
          // own truth and repaint with this receipt. Without the re-read the
          // page would go on offering Issue for a key it has just confirmed
          // (round 3).
          await refreshAfterAction("api_key", node);
          return;
        }
        pinned = { key: "api_key", node };
        paint();
      })();
    });
    return button;
  }

  async function issueInvitation(scope: HTMLElement): Promise<void> {
    const key = `checklist-link-invite:${plainSlug(company)}`;
    const heldLanding: {
      value: { issued: { id: string; token: string }; hostedLinkUrl: string } | null;
    } = { value: null };
    const heldUnrevoked: { id: string | undefined } = { id: undefined };
    const outcome = await runWrite({
      key,
      scope,
      gate: () => null,
      send: async () => {
        const issued = await client.issueLinkInvitation(company, {
          expiresAt: new Date(Date.now() + INVITE_LIFETIME_MS).toISOString(),
          label: INVITE_LABEL,
        });
        try {
          const session = await client.openLinkSession(issued.token);
          return { issued, hostedLinkUrl: session.hostedLinkUrl };
        } catch (error) {
          // The invitation exists but the link could not be opened. Revoke it
          // so the retry cannot leave a live invitation behind; if that revoke
          // fails, say so rather than swallowing it (rc-2 sister 5).
          try {
            await client.revokeLinkInvitation(issued.id);
          } catch {
            heldUnrevoked.id = issued.id;
          }
          throw error;
        }
      },
      afterLanded: (value) => {
        heldLanding.value = value;
      },
      stillCurrent,
      blockWhenPossiblySent: true,
    });
    if (outcome.kind === "landed" && !stillCurrent()) {
      // The issue landed after the page moved on, so the link is gone. The
      // next visit must take the check path, never offer a fresh issue
      // (round 2 M2).
      rememberPossiblySent(key);
      return;
    }
    if (!stillCurrent()) return;
    if (outcome.kind === "landed" && heldLanding.value !== null) {
      const done = heldLanding.value;
      const receipt: OnboardingReceipt = invitationIssuedReceipt(company);
      const node = renderReceiptCard(receipt, {
        words: face.receiptWords(receipt, directory, role),
        oneTime: { label: "Invitation link (copy it now)", value: done.hostedLinkUrl },
        oneTimeRole: "receipt-one-time-invitation",
      });
      await refreshAfterAction("link_invitation", node);
      return;
    }
    if (outcome.kind === "landed") return;
    pinned = { key: "link_invitation", node: issueFailureFromOutcome(outcome) };
    if (heldUnrevoked.id !== undefined) {
      // The invitation may still be live: say so and offer the revoke again.
      pinned.node.append(
        h("p", { class: "muted" }, [
          "The invitation was created and could not be revoked, so it may still be live.",
        ]),
        offerRevokeAgain(heldUnrevoked.id),
      );
    } else if (outcome.kind === "unknown") {
      pinned.node.append(offerForget(key));
    }
    paint();
  }

  /** After a failed revoke: the invitation may still be live; offer it again. */
  function offerRevokeAgain(id: string): HTMLElement {
    const button = h(
      "button",
      {
        type: "button",
        class: "btn btn-secondary",
        "data-action": "revoke-invitation-again",
      },
      ["Revoke the invitation"],
    );
    button.addEventListener("click", () => {
      void (async () => {
        button.disabled = true;
        const outcome = await runWrite({
          key: `checklist-link-revoke:${plainSlug(id)}`,
          scope: button.parentElement ?? root,
          gate: () => null,
          send: () => client.revokeLinkInvitation(id),
          stillCurrent,
        });
        if (!stillCurrent()) return;
        if (outcome.kind === "landed") {
          // A revoke that landed is a success, not a red failure card
          // (round 2 M7).
          pinned = {
            key: "link_invitation",
            node: renderReceiptCard(
              {
                happened: INVITATION_REVOKED_COPY,
                unchanged: "No other settings changed.",
                next: null,
              },
              {
                words: {
                  happened: INVITATION_REVOKED_COPY,
                  unchanged: "No other settings changed.",
                  next: null,
                },
              },
            ),
          };
          paint();
          return;
        }
        pinned = {
          key: "link_invitation",
          node: renderReceiptFailure(
            "Could not revoke the invitation.",
            unchangedFor(outcome),
          ),
        };
        paint();
      })();
    });
    return button;
  }

  /** After a lost answer on an invitation: the person checks, then may retry. */
  function offerForget(key: string): HTMLElement {
    const button = h(
      "button",
      {
        type: "button",
        class: "btn btn-secondary",
        "data-action": "checked-link-invite",
      },
      ["I checked — try again"],
    );
    button.addEventListener("click", () => {
      forgetPossiblySent(key);
      pinned = {
        key: "link_invitation",
        node: renderReceiptFailure(
          unchangedFor({ kind: "refused", status: 0 }),
          "Press Issue again when you are ready.",
        ),
      };
      paint();
    });
    return button;
  }

  /**
   * After a successful owner action, run check so the row's evidence updates.
   * The receipt (which may carry a one-time secret) is pinned to that row for
   * this render only; the next render does not receive it.
   */
  async function refreshAfterAction(
    key: OnboardingStepKey,
    node: HTMLElement,
  ): Promise<void> {
    const held: { value: OnboardingRunResponse | undefined } = { value: undefined };
    const outcome = await runWrite({
      key: `checklist-refresh:${plainSlug(company)}:${key}`,
      scope: root,
      gate: () => null,
      send: () => client.checkOnboardingRun(company),
      afterLanded: (response) => {
        held.value = response;
      },
      stillCurrent,
    });
    if (!stillCurrent()) return;
    // A landed refresh updates the recorded truth; a failed one is not the
    // action's failure, so the receipt below stays exactly as it was.
    if (outcome.kind === "landed" && held.value !== undefined) {
      status = held.value.status;
      lastCheckedAt = new Date().toISOString();
    }
    // The action succeeded; a failed refresh must not erase its receipt.
    pinned = { key, node };
    paint();
  }

  async function resolveCompanyType(): Promise<CompanyType | undefined> {
    if (companyType !== undefined) return companyType;
    try {
      const { companies } = await client.listCompanies();
      const match = companies.find((row: Company) => row.slug === company);
      companyType = match?.companyType;
    } catch {
      // Fall through: issueToken still decides server-side. No guess beyond
      // the policy's own default (external/core) if the list is unavailable.
    }
    return companyType;
  }

  function groupSteps(
    steps: readonly OnboardingChecklistStep[],
  ): { name: string; steps: OnboardingChecklistStep[] }[] {
    const groups: { name: string; steps: OnboardingChecklistStep[] }[] = [];
    for (const step of steps) {
      let group = groups.find((row) => row.name === step.group);
      if (group === undefined) {
        group = { name: step.group, steps: [] };
        groups.push(group);
      }
      group.steps.push(step);
    }
    // The contract says core first, then the company's own groups in order.
    const core = groups.filter((group) => group.name === "core");
    const rest = groups.filter((group) => group.name !== "core");
    return [...core, ...rest];
  }
}

/** The next-step block only needs the fields as WORDS, not the closed step type. */
interface NextWords {
  key: OnboardingStepKey;
  who: string;
  needs: "approval" | "action";
}

/**
 * The header's next-step block. "Your next step" is only the reader's own step
 * (rc-4 A3); a step waiting on someone else says whose it is, and the account
 * manager reads the decided "Waiting for the owner" on the owner's step. When
 * the Worker sends no next step, the block shows `checklistView`'s one line, so
 * it can never claim everything is done while required rows are open (rc-4 A2).
 */
function nextBlock(
  next: NextWords | null,
  view: ReturnType<typeof checklistView>,
  role: StaffRole,
): HTMLElement {
  const host = h("section", {
    class: "checklist-next",
    "data-role": "checklist-next",
  });
  if (next === null) {
    const tail =
      view.openRequired > 0
        ? ""
        : " Go live is the remaining action and is not built yet. There is nothing else to do on this list.";
    host.append(
      h("p", { class: "empty-state", "data-role": "checklist-next-empty" }, [
        `${view.nextLine}${tail}`,
      ]),
    );
    return host;
  }
  const mine = next.who === (role === "owner" ? "owner" : "am");
  const who =
    next.key === "accounts_mapped"
      ? ACCOUNTS_MAPPED_WHO
      : (face.WHO_WORDS[next.who] ?? "the person on that step");
  const label =
    next.key === "accounts_mapped"
      ? who
      : mine
        ? "Your next step"
        : next.who === "owner"
          ? sentence("waiting_for_owner", role)
          : who;
  const actor = next.key === "accounts_mapped" ? ACCOUNTS_MAPPED_WHO : mine ? "you" : who;
  host.append(
    h("p", { class: "checklist-next-label" }, [label]),
    h("p", { class: "checklist-next-title", "data-role": "checklist-next-title" }, [
      checklistStepTitle(next.key),
    ]),
    h("p", { class: "muted", "data-role": "checklist-next-owner" }, [
      `${actor} · ${needsCopy(next)}`,
    ]),
  );
  return host;
}

/**
 * The slug as a plain string, for request ids that are NOT words.
 *
 * The brand guard's rule is that a branded value never sits in a plain
 * position, and it cannot tell a request key from a sentence. A Map is its one
 * stated subtraction for "this is a key, not words" (`Map/Set key or lookup`),
 * so the id is read back out of one instead of being interpolated directly.
 */
function plainSlug(slug: CompanySlug): string {
  const box = new Map<CompanySlug, string>();
  box.set(slug, slug);
  return box.get(slug) ?? "";
}

function needsCopy(next: { needs: "approval" | "action" }): string {
  return next.needs === "approval" ? "needs an approval" : "needs an action";
}

/** The contract's `who` words; the owner reads their own step as "you". */
function actorWords(
  step: OnboardingChecklistStep,
  view: ChecklistStepView,
  role: StaffRole,
): string {
  if (step.key === "accounts_mapped") return ACCOUNTS_MAPPED_WHO;
  switch (view.waiter) {
    case "you":
      return role === "account_manager" ? "the account manager (you)" : "the owner (you)";
    case "owner":
      return "the owner";
    case "system":
      return "the system";
    case "hub":
      return "the hub";
  }
  // Not a waiter this reader can act on, so `waiter` is null: name the
  // contract's own `who` in words (a bank linker, or an `am` row read by the
  // owner) instead of a role-blind "someone" (round 2 M6).
  return face.WHO_WORDS[step.who] ?? "the person on that step";
}

function waitingCopy(step: OnboardingChecklistStep, view: ChecklistStepView, role: StaffRole): string {
  if (view.state === "done") {
    // The catalogue's `who` says who the row is assigned to, not who did it;
    // an owner-confirmed hub row read "Done by the hub team" (rd R11 R6-16).
    return "Done.";
  }
  if (HAND_STEP_KEYS.has(step.key) && role !== "owner") {
    // A hand step's ONE waiter, to a reader who cannot tick it, is the owner
    // (round 2 M5); the action row below adds no second line.
    return `${sentence("waiting_for_owner", role)}.`;
  }
  if (OUTSIDE_CONSOLE_STEP_KEYS.has(step.key) && view.state !== "failed") {
    // rd r13 L3.4: no console control completes this row. Saying "waiting on
    // the owner (needs an approval)" above "Nothing is waiting for approval"
    // anywhere else was a dead end; name who does it and where.
    return `Waiting on the system. ${sentence("switched_outside_console", role)}`;
  }
  if (view.state === "failed") {
    return `Checked by the system; it needs another try.`;
  }
  if (view.state === "blocked") {
    const blockedTitles = step.blockedBy
      .map((key) => checklistStepTitle(key))
      .join(", ");
    return `Waiting on ${actorWords(step, view, role)} — blocked by ${blockedTitles}.`;
  }
  return `Waiting on ${actorWords(step, view, role)} (${needsCopy(step)}).`;
}

export function evidenceCopy(step: OnboardingChecklistStep): string {
  const when = step.checkedAt === null ? "" : ` ${evidenceWhen(step.checkedAt)}`;
  if (step.evidence === "system_verified") {
    return `Checked by the system${when}.`;
  }
  if (step.evidence === "owner_confirmed") {
    return `Marked done by the owner, not checked${when}.`;
  }
  return "Marked done, but no evidence was recorded.";
}

/**
 * When a check ran, in the page's own time words (rd r13 L3.5). A check that
 * just ran reads "just now", not a bare calendar day that looks like it
 * happened days ago; a recent one keeps the same relative phrase, and an older
 * one keeps the decided `Oct 18, 2026` date so it never drifts with the
 * reader's clock.
 */
function evidenceWhen(iso: string): string {
  const recent = timeAgoText(iso);
  if (recent === "just now" || recent === "yesterday" || /^\d+ (minute|hour)s? ago$/u.test(recent)) {
    return recent;
  }
  return dateText(iso) || formatWhen(iso);
}

/**
 * A date as a person reads it (`Oct 18, 2026`). The old form printed
 * `2026-10-18 10:05 UTC`, which the decided wording rule forbids on screen.
 * An unreadable value says so rather than showing the raw string.
 */
export function formatWhen(iso: string): string {
  const shown = dateText(iso);
  return shown === "" ? "an unknown time" : shown;
}
